import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Button, Form, Image, Label, Modal, Table } from 'semantic-ui-react'
import close_modal from '../../../../assets/svg/close_modal.svg'
import DatepickerComponent from '../../../../components/Datepicker/Datepicker'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { tryParseToDateOrUndefined } from '../../../../utils/dateParseUtil'
import { taskDisplayName } from '../../../../utils/formatters'
import { _initialMilestone } from '../../reducers/directAndPassTroughCostsReducer'
import { IMilestone, IMilestoneOfferDirectCost } from '../../types/directAndPassTroughCosts'
import { IProject, IProjectPhase } from '../../types/project'


export interface IMilestoneTaskAssign extends IMilestoneOfferDirectCost {
  selected: boolean
}

interface Props {
  project: IProject
  unassignedTasks: IMilestoneOfferDirectCost[]
  milestone: IMilestone
  open: boolean
  saveMilestone: (milestone: IMilestone) => Promise<IMilestone>
  closeModal: () => void
}

const HEADER = [
  { text: 'Task', padding: '6px 0 6px 12px', width: '250px' },
  { text: 'Phase', padding: '6px 0 6px 0', width: '177px' },
  { text: 'Department', padding: '6px 0 6px 0', width: '177px' },
  { text: '', padding: '6px 0 6px 0', width: '100%' }
]

const MilestoneModalComponent = (props: Props): JSX.Element => {
  const {
    unassignedTasks,
    milestone,
    open,
    project,
    saveMilestone,
    closeModal
  } = props

  const [milestoneState, setMilestoneState] = useState(milestone)
  const [openDatepicker, setOpenDatepicker] = useState(false)
  const [tasks, setTasks] = useState([...milestone.tasks.map(t => ({ ...t, selected: true })), ...unassignedTasks.map(t => ({ ...t, selected: false }))])
  const [validateLenName, setValidateLenName] = useState(false)
  const [validate, setValidate] = useState(false)

  const maxLength25ValidationRule = new RegExp("^.{0,25}$")
  const invalidMaxLength25ValidationRuleMsg = 'Max number of characters exceeded. There are only 25 chars allowed.'
  const maxLength26 = 26

  useEffect(() => {
    setMilestoneState(milestone)
    setValidateLenName(milestone.name.length < maxLength26)
    setValidate(moment(milestone.date, 'YYYY-MM-DD').isValid())
  }, [milestone])

  useEffect(() => {
    setTasks([...milestone.tasks.map(t => ({ ...t, selected: true })), ...unassignedTasks.map(t => ({ ...t, selected: false }))])
  }, [unassignedTasks, milestone])

  const handleClose = () => {
    setMilestoneState(_initialMilestone)
    setValidateLenName(false)
    setValidate(false)
    closeModal()
  }

  const renderClose = () => {
    return (
      <Image src={close_modal} onClick={handleClose} style={{ margin: '3% 0 0 95%', cursor: 'pointer' }} />
    )
  }

  const renderModalContent = () => {
    const date = tryParseToDateOrUndefined(milestoneState.date)
    const start_date = tryParseToDateOrUndefined(project.start_date)
    const end_date = tryParseToDateOrUndefined(project.end_date)
    return (
      <Form>
        <Form.Group>
          <ValidationInput
            fluid
            value={milestoneState.name}
            onChange={(e, v) => (setMilestoneState(prev => ({ ...prev, name: e.target.value })), setValidateLenName(v ?? true))}
            placeholder='Enter Milestone'
            fieldWidth={8}
            label={
              <label style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: 2, backgroundColor: 'white' }}>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Milestone</p>
              </label>
            }
            height='3.571rem'
            paddingLeft='6px'
            paddingRight='6px'
            marginTop='2rem'
            zIndex={1}
            validatePattern={maxLength25ValidationRule}
            invalidMessage={invalidMaxLength25ValidationRuleMsg}
            maxLength={maxLength26}
          />
          <div style={{ width: '50%', padding: '0 6px' }}>
            <label style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: 2, backgroundColor: 'white' }}>
              <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Date</p>
            </label>
            <DatepickerComponent
              inputColor='transparent-color'
              inputBorder={true}
              dateFormat='dd.MM.yyyy'
              inputWidth='100%'
              inputMargin
              inputHeight='3.571rem'
              inputBorderSmall={false}
              open={openDatepicker}
              onClickOutside={() => setOpenDatepicker(false)}
              onInputClick={() => setOpenDatepicker(!openDatepicker)}
              onChange={(date: Date) => setMilestoneState(prev => ({ ...prev, date: date }))}
              selected={date}
              placeholder='Enter Date'
              validation
              returnOnValidation={v => setValidate(v)}
              minDate={start_date}
              maxDate={end_date}
            />
          </div>
        </Form.Group>
      </Form>
    )
  }

  const renderTableHeader = () => {
    return HEADER.map((h, idx) => {
      return (
        <Table.HeaderCell
          textAlign={h.text === 'Phase' ? 'center' : 'left'}
          key={idx}
          style={{
            color: '#747678',
            padding: h.padding,
            border: 'none',
            minWidth: h.width,
            maxWidth: h.width,
            width: h.width
          }}
        >{h.text}
        </Table.HeaderCell>
      )
    })
  }

  const renderCheckbox = (checked: boolean, taskId: number) => {
    if (checked) {
      return (
        <Button
          onClick={() => setTasks(prev => ([...prev.map(t => t.offer_direct_cost.id === taskId ? { ...t, selected: false } : t)]))}
          style={{
            height: '20px',
            width: '20px',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '20px',
            boxShadow: '0 0 25px -5px rgba(21,21,21,0.1)'
          }}
          primary
          icon='check'
        />
      )
    }
    return (
      <div
        onClick={() => setTasks(prev => ([...prev.map(t => t.offer_direct_cost.id === taskId ? { ...t, selected: true } : t)]))}
        style={{
          cursor: 'pointer',
          marginRight: '20px',
          background: '#FFFFFF',
          height: '22px',
          width: '22px',
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 0 25px -5px rgba(21,21,21,0.1)',
          border: '1px solid #E5EBF0'
        }}
      />
    )
  }

  const renderPhaseLabel = (phase: IProjectPhase) => {
    const _upperCase = phase.name.toUpperCase() === phase.name
    const _nameLen = _upperCase ? phase.name.length > 15 ? phase.name.slice(0, 14).concat('...') : phase.name : phase.name.length > 19 ? phase.name.slice(0, 17).concat('...') : phase.name
    return (
      <Label
        key={phase.id}
        horizontal
        style={{
          borderRadius: '.6rem',
          color: phase.color,
          backgroundColor: phase.color + '33',
          fontSize: '.8rem',
          height: '1.7rem',
          minWidth: 'auto'
        }}
        content={_nameLen}
      />
    )
  }

  const renderTableBody = () => {
    return tasks.map((t, idx) => {
      return (
        <Table.Row key={idx} style={{ borderTop: idx === 0 ? 'none' : '', display: 'table', tableLayout: 'inherit' }}>
          <Table.Cell style={{ display: 'flex', alignItems: 'center', padding: '14px 0 14px 12px', width: '250px', minWidth: '250px', maxWidth: '250px' }}>
            {taskDisplayName(t.offer_direct_cost.key, t.offer_direct_cost.offer_position)}
          </Table.Cell>
          <Table.Cell textAlign='center' style={{ fontSize: '12px', color: '#172533', letterSpacing: '.25px', padding: '14px 0', width: '177px', minWidth: '177px', maxWidth: '177px' }}>
            {renderPhaseLabel(t.offer_direct_cost.project_phase)}
          </Table.Cell>
          <Table.Cell style={{ fontSize: '12px', color: '#172533', letterSpacing: '.25px', padding: '14px 0', width: '177px', minWidth: '177px', maxWidth: '177px' }}>
            {t.offer_direct_cost.department.name}
          </Table.Cell>
          <Table.Cell style={{ fontSize: '12px', color: '#172533', letterSpacing: '.25px', padding: '14px 0', width: 'auto' }}>
            {renderCheckbox(t.selected, +t.offer_direct_cost.id)}
          </Table.Cell>
        </Table.Row>
      )
    })
  }

  const renderTaskAssignment = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: 2, backgroundColor: 'white' }}>
          <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Assign Tasks</p>
        </label>
        <Table unstackable basic='very' style={{ padding: 0, margin: '2rem 0' }}>
          <Table.Header style={{ height: '40px', backgroundColor: '#F4F7F9', display: 'table', tableLayout: 'inherit' }}><Table.Row>{renderTableHeader()}</Table.Row></Table.Header>
          <Table.Body style={{ display: 'block', height: '260px', overflow: 'hidden auto' }}>{renderTableBody()}</Table.Body>
        </Table>
      </div>
    )
  }

  const renderModal = () => {
    return (
      <Modal
        closeIcon={renderClose()}
        open={open}
        style={{ width: '54rem' }}
      >
        <Modal.Header style={{ fontSize: '2rem', paddingLeft: '4rem' }} as='h1'>
          Add Tasks to Milestone
        </Modal.Header>
        <Modal.Content style={{ padding: '4px 4rem' }}>
          {renderModalContent()}
          {renderTaskAssignment()}
        </Modal.Content>
        <Modal.Actions style={{ background: '#F9FBFC', padding: '1.5rem' }}>
          <Button
            onClick={() => saveMilestone({
              ...milestoneState,
              tasks: tasks.filter(t => t.selected),
              date: moment(milestoneState.date).format('YYYY-MM-DD')
            }).then(() => handleClose())}
            content='Save'
            color='green'
            disabled={!(milestoneState.name !== '' && validateLenName && validate && milestoneState.date !== '')}
            style={{
              width: '8.571rem',
              height: '2.143rem',
              borderRadius: '.34rem',
              padding: 0,
              margin: 0,
              marginRight: '4rem'
            }}
          />
        </Modal.Actions>
      </Modal>
    )
  }

  return renderModal()
}

export default MilestoneModalComponent
